import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
// Nouveau service unifié DailySales
import {
  DailySalesService,
  ProductSaleEntry,
  ProductSale,
  DailySalesPayload,
} from '../../../services/daily-sales.service';
// Service recommandations pour récupérer les quantités recommandées
import { RecommendationsService } from '../../../services/recommendation.service';
import { ProductsService } from '../../../services/product.service';
import { AuthService } from '../../../services/auth.service';
import { Product } from '../../../models/product.model';
import { LogService } from '../../../services/log.service';
import { AnalyticsService } from '../../../services/analytics.service';

@Component({
  selector: 'app-sales-entry-fixed',
  imports: [CommonModule, FormsModule],
  templateUrl: './sales-entry-fixed.component.html',
  styleUrls: ['./sales-entry-fixed.component.css'],
})
export class SalesEntryFixedComponent implements OnInit {
  private log = inject(LogService);
  private analytics = inject(AnalyticsService);
  private cdr = inject(ChangeDetectorRef);

  step: 'select-date' | 'enter-sales' | 'context' | 'summary' = 'select-date';

  // Étape 1: Sélection période + services
  periodType: 'today' | 'yesterday' | 'custom' = 'today';
  selectedDate: string = '';
  customPeriod = {
    from: '',
    to: '',
  };

  // Services multiples avec horaires
  availableServices = [
    {
      id: 'Petit-déjeuner',
      label: 'Matin (7h-12h)',
      name: 'Petit-déjeuner',
      checked: false,
    },
    {
      id: 'Déjeuner',
      label: 'Midi (12h-15h)',
      name: 'Déjeuner',
      checked: false,
    },
    { id: 'Dîner', label: 'Soir (18h-21h)', name: 'Dîner', checked: false },
  ];

  selectedServices: string[] = [];
  expandedService: string | null = null; // Quel service est déplié dans l'accordéon

  // Étape 2: Saisie produits
  products: Product[] = [];
  productSales: ProductSale[] = [];
  productSalesByService: Map<string, ProductSale[]> = new Map();

  // Étape 3: Contexte
  contextFlags = {
    ruptureStock: false,
    unusualAffluence: false,
    weatherImpact: false,
    localEvent: false,
    technicalIssue: false,
    differentLocation: false,
  };
  notes: string = '';
  actualAttendance: string = '';
  actualWeather: string = '';

  // État
  loading: boolean = false;
  error: string = '';
  successMessage: string = '';
  isDraft: boolean = false;

  // Recommandations calculées par le moteur métier/IA
  // Map: serviceId -> Map<productId, recommendedQuantity>
  recommendationsByService: Map<string, Map<string, number>> = new Map();
  recommendationsLoaded: boolean = false;

  // Max date = aujourd'hui
  maxDate: string = '';
  yesterdayDate: string = '';

  // Paramètres de navigation (queryParams)
  private queryDate: string | null = null;
  private queryService: string | null = null;
  private queryStep: string | null = null;

  constructor(
    private dailySalesService: DailySalesService,
    private recommendationsService: RecommendationsService,
    private productsService: ProductsService,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
  ) {
    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0];

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    this.yesterdayDate = yesterday.toISOString().split('T')[0];
  }

  ngOnInit(): void {
    // Lire les paramètres de l'URL (queryParams)
    this.route.queryParams.subscribe((params) => {
      this.queryDate = params['date'] || null;
      this.queryService = params['service'] || null;
      this.queryStep = params['step'] || null;

      this.log.debug('📍 QueryParams:', {
        date: this.queryDate,
        service: this.queryService,
        step: this.queryStep,
      });
    });

    // Initialiser date du jour ou depuis queryParams
    const today = new Date();
    if (this.queryDate) {
      this.selectedDate = this.queryDate;
      this.periodType = 'custom';
      this.customPeriod.from = this.queryDate;
      this.customPeriod.to = this.queryDate;
    } else {
      this.selectedDate = today.toISOString().split('T')[0];
    }

    // Filtrer les services disponibles selon le profil utilisateur
    const user = this.authService.getCurrentUser();
    this.log.debug('👤 User profile:', user);
    this.log.debug('📋 User services:', user?.services);
    this.log.debug('📋 Premier service structure:', user?.services?.[0]);

    if (user && user.services && user.services.length > 0) {
      this.log.debug('✅ Filtrage des services selon le profil');
      // Garder seulement les services configurés dans le profil
      const filteredServices = this.availableServices.filter((service) => {
        const found = user.services!.some((userService: any) => {
          // Essayer différentes propriétés possibles
          const serviceName =
            userService.serviceName || userService.name || userService;
          this.log.debug(
            `Comparaison: ${serviceName} === ${service.id}?`,
            serviceName === service.id,
          );
          return serviceName === service.id;
        });
        return found;
      });

      this.log.debug('📊 Services filtrés:', filteredServices);
      this.availableServices = filteredServices;

      // Si un service spécifique est passé en queryParam, cocher uniquement celui-là
      if (this.queryService) {
        this.availableServices.forEach((service) => {
          service.checked =
            service.id === this.queryService ||
            service.name === this.queryService;
        });
      } else {
        // Cocher tous les services disponibles par défaut
        this.availableServices.forEach((service) => (service.checked = true));
      }
    } else {
      this.log.debug(
        '⚠️ Pas de services dans le profil, utilisation du fallback',
      );
      // Fallback: cocher Petit-déjeuner et Déjeuner si pas de services configurés
      if (this.availableServices[0]) this.availableServices[0].checked = true;
      if (this.availableServices[1]) this.availableServices[1].checked = true;
    }

    this.log.debug('🎯 availableServices final:', this.availableServices);
    this.updateSelectedServices();
    this.loadProducts();

    // Si step=products, aller directement à l'étape produits après chargement
    if (this.queryStep === 'products') {
      // Attendre un tick pour que les services soient initialisés
      setTimeout(() => this.goToProductsStep(), 100);
    }
  }

  /**
   * Aller directement à l'étape produits (après validation automatique date/service)
   */
  goToProductsStep(): void {
    // S'assurer qu'au moins un service est sélectionné
    if (this.selectedServices.length === 0) {
      this.updateSelectedServices();
    }

    // Passer directement à l'étape de saisie via la méthode standard
    if (this.selectedServices.length > 0 && this.selectedDate) {
      this.log.debug('🚀 Navigation directe vers la saisie produits');
      this.proceedToSalesEntry();
    }
  }

  /**
   * Charger les recommandations calculées pour la date sélectionnée
   */
  loadRecommendations(): void {
    this.recommendationsService.getTodayRecommendationsFromAPI().subscribe({
      next: (response) => {
        this.recommendationsByService.clear();
        if (response.success && response.services) {
          response.services.forEach((service) => {
            const productMap = new Map<string, number>();
            service.products.forEach((product: any) => {
              productMap.set(
                product.productId.toString(),
                product.recommendedQuantity,
              );
            });
            this.recommendationsByService.set(service.serviceName, productMap);
          });
        }
        this.recommendationsLoaded = true;
        this.log.debug(
          '✅ Recommandations chargées:',
          this.recommendationsByService.size,
          'services',
        );
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.log.error('❌ Erreur chargement recommandations:', err);
        // Continuer sans recommandations
        this.recommendationsLoaded = true;
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Obtenir la recommandation pour un produit et un service
   */
  getRecommendedQuantity(
    productId: string,
    serviceName?: string,
  ): number | null {
    if (serviceName) {
      const serviceRecos = this.recommendationsByService.get(serviceName);
      if (serviceRecos) {
        return serviceRecos.get(productId) || null;
      }
    }
    // Si pas de service spécifié, chercher dans tous les services
    for (const [, productMap] of this.recommendationsByService) {
      const reco = productMap.get(productId);
      if (reco !== undefined) return reco;
    }
    return null;
  }

  /**
   * Mettre à jour la date selon le type de période sélectionné
   */
  updateDateFromPeriodType(): void {
    const today = new Date();

    if (this.periodType === 'today') {
      this.selectedDate = today.toISOString().split('T')[0];
    } else if (this.periodType === 'yesterday') {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      this.selectedDate = yesterday.toISOString().split('T')[0];
    }
    // Si custom, l'utilisateur saisit manuellement
  }

  /**
   * Mettre à jour la liste des services sélectionnés
   */
  updateSelectedServices(): void {
    this.selectedServices = this.availableServices
      .filter((s) => s.checked)
      .map((s) => s.name);
  }

  /**
   * Toggle un service
   */
  toggleService(serviceId: string): void {
    const service = this.availableServices.find((s) => s.id === serviceId);
    if (service) {
      service.checked = !service.checked;
      this.updateSelectedServices();
    }
  }

  /**
   * Basculer l'accordéon d'un service
   */
  toggleAccordion(serviceId: string): void {
    this.expandedService =
      this.expandedService === serviceId ? null : serviceId;
  }

  /**
   * Obtenir les produits pour un service donné
   */
  getProductsForService(serviceId: string): ProductSale[] {
    return this.productSalesByService.get(serviceId) || [];
  }

  /**
   * Charger les produits du catalogue
   */
  loadProducts(): void {
    this.productsService.getMyProducts().subscribe({
      next: (response: any) => {
        this.products = response.data || response;
        this.log.debug('Produits chargés:', this.products.length);
        this.cdr.markForCheck();
      },
      error: (err: any) => {
        this.log.error('Erreur chargement produits:', err);
        this.error = 'Impossible de charger les produits';
        this.cdr.markForCheck();
      },
    });
  }

  /**
   * Étape 1 → 2: Valider date + services et charger draft ou créer saisie vide
   */
  proceedToSalesEntry(): void {
    // Valider selon type de période
    if (this.periodType === 'custom') {
      if (!this.customPeriod.from || !this.customPeriod.to) {
        this.error = 'Veuillez sélectionner une période complète';
        return;
      }
      if (new Date(this.customPeriod.from) > new Date(this.customPeriod.to)) {
        this.error = 'La date de début doit être avant la date de fin';
        return;
      }
      // Pour l'instant, on prend juste la date 'from' (TODO: gérer multi-dates)
      this.selectedDate = this.customPeriod.from;
    }

    if (!this.selectedDate) {
      this.error = 'Veuillez sélectionner une date';
      return;
    }

    if (this.selectedServices.length === 0) {
      this.error = 'Veuillez sélectionner au moins un service';
      return;
    }

    // Vérifier que la date est passée
    if (new Date(this.selectedDate) > new Date()) {
      this.error = 'Impossible de saisir des ventes pour une date future';
      return;
    }

    this.loading = true;
    this.error = '';

    // D'abord charger les recommandations, puis les ventes existantes
    this.loadRecommendationsAndSales();
  }

  /**
   * Charger d'abord les recommandations, puis les ventes existantes
   */
  loadRecommendationsAndSales(): void {
    this.log.debug(
      '📅 Chargement des données pour la date:',
      this.selectedDate,
    );

    // Charger les recommandations d'abord
    this.recommendationsService
      .getFixedRecommendationsFromAPI(this.selectedDate)
      .subscribe({
        next: (response) => {
          this.recommendationsByService.clear();
          if (response.success) {
            // Format avec plusieurs services
            if (response.recommendations) {
              response.recommendations.forEach((rec) => {
                const productMap = new Map<string, number>();
                rec.products.forEach((p: any) => {
                  productMap.set(p.productId.toString(), p.recommendedQuantity);
                });
                this.recommendationsByService.set(rec.serviceName, productMap);
              });
            }
            // Format avec un seul service
            if (response.products && response.serviceName) {
              const productMap = new Map<string, number>();
              response.products.forEach((p: any) => {
                productMap.set(p.productId.toString(), p.recommendedQuantity);
              });
              this.recommendationsByService.set(
                response.serviceName,
                productMap,
              );
            }
          }
          this.recommendationsLoaded = true;
          this.log.debug(
            '✅ Recommandations chargées:',
            this.recommendationsByService.size,
            'services',
          );
          // Maintenant charger les ventes existantes
          this.loadExistingSalesForAllServices();
          this.cdr.markForCheck();
        },
        error: (err) => {
          this.log.warn('⚠️ Erreur chargement recommandations:', err);
          this.recommendationsLoaded = true;
          // Continuer sans recommandations
          this.loadExistingSalesForAllServices();
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Charger les ventes existantes pour tous les services sélectionnés
   */
  loadExistingSalesForAllServices(): void {
    // Initialiser la structure par service
    this.productSalesByService.clear();

    // Créer un compteur pour savoir quand tous les services sont chargés
    let loadedCount = 0;
    const totalServices = this.selectedServices.length;

    this.selectedServices.forEach((serviceName) => {
      this.log.debug(
        `📊 Chargement des ventes pour ${serviceName} à la date ${this.selectedDate}`,
      );

      // Chercher les ventes existantes pour ce service et cette date
      this.dailySalesService
        .list({
          merchantType: 'fixed',
          from: this.selectedDate,
          to: this.selectedDate,
          serviceName: serviceName, // Utiliser le nom du service
          isDraft: false,
        })
        .subscribe({
          next: (response) => {
            if (response.data && response.data.length > 0) {
              // Ventes existantes trouvées, les mapper
              const existingSale = response.data[0];
              const productsForService = this.mapExistingSaleToProducts(
                existingSale,
                serviceName,
              );
              this.productSalesByService.set(serviceName, productsForService);
              this.log.debug(
                `✅ Ventes existantes chargées pour ${serviceName}:`,
                productsForService.length,
              );
            } else {
              // Pas de ventes existantes, initialiser vide avec recommandations
              const productsForService =
                this.initializeEmptyProductsForService(serviceName);
              this.productSalesByService.set(serviceName, productsForService);
              this.log.debug(`📋 Saisie vide initialisée pour ${serviceName}`);
            }

            loadedCount++;
            if (loadedCount === totalServices) {
              this.finalizeSalesEntry();
            }
            this.cdr.markForCheck();
          },
          error: (err) => {
            this.log.error(
              `Erreur chargement ventes pour ${serviceName}:`,
              err,
            );
            // En cas d'erreur, initialiser vide
            const productsForService =
              this.initializeEmptyProductsForService(serviceName);
            this.productSalesByService.set(serviceName, productsForService);

            loadedCount++;
            if (loadedCount === totalServices) {
              this.finalizeSalesEntry();
            }
            this.cdr.markForCheck();
          },
        });
    });
  }

  /**
   * Mapper les ventes existantes vers le format ProductSale
   * R1: Si pas de vente existante, prepared = Product.quantity par défaut
   * Charge aussi les infos contextuelles (notes, météo, affluence)
   */
  mapExistingSaleToProducts(sale: any, serviceName: string): ProductSale[] {
    // Charger les infos contextuelles si présentes
    if (sale.contextFlags) {
      this.contextFlags = {
        ruptureStock: sale.contextFlags.ruptureStock || false,
        unusualAffluence: sale.contextFlags.unusualAffluence || false,
        weatherImpact: sale.contextFlags.weatherImpact || false,
        localEvent: sale.contextFlags.localEvent || false,
        technicalIssue: sale.contextFlags.technicalIssue || false,
        differentLocation: sale.contextFlags.differentLocation || false,
      };
    }

    if (sale.notes) {
      this.notes = sale.notes;
    }

    if (sale.actualAttendance) {
      this.actualAttendance = sale.actualAttendance;
    }

    if (sale.actualWeather) {
      this.actualWeather = sale.actualWeather;
    }

    this.log.debug(`📋 Infos contextuelles chargées pour ${serviceName}:`, {
      contextFlags: this.contextFlags,
      notes: this.notes,
      actualAttendance: this.actualAttendance,
      actualWeather: this.actualWeather,
    });

    return this.products.map((product) => {
      const productId = (product as any)._id || '';
      // Chercher ce produit dans les ventes existantes
      const existingProduct = sale.products?.find(
        (p: any) => p.productId === productId || p.productId?._id === productId,
      );

      // Récupérer la recommandation calculée pour ce service
      const recommended = this.getRecommendedQuantity(productId, serviceName);
      // Utiliser la recommandation si disponible, sinon 0 (pas product.quantity qui est le stock total)
      const defaultPrepared = recommended ?? 0;

      if (existingProduct) {
        return {
          productId: productId,
          productName: product.name,
          recommended: recommended, // Recommandation calculée
          prepared: existingProduct.quantityPrepared ?? defaultPrepared,
          sold: existingProduct.quantitySold ?? 0,
          wasted: existingProduct.quantityWasted ?? 0,
          hasRupture:
            existingProduct.hadRupture || existingProduct.hasRupture || false,
        };
      } else {
        return {
          productId: productId,
          productName: product.name,
          recommended: recommended, // Recommandation calculée
          prepared: defaultPrepared, // Recommandation ou 0
          sold: 0,
          wasted: 0,
          hasRupture: false,
        };
      }
    });
  }

  /**
   * Initialiser les produits vides pour un service
   * Si recommandation IA disponible, l'utiliser comme valeur par défaut
   * Sinon, laisser prepared à 0 (l'utilisateur doit saisir)
   */
  initializeEmptyProductsForService(serviceName: string): ProductSale[] {
    return this.products.map((product) => {
      const productId = (product as any)._id || '';
      // Récupérer la recommandation calculée pour ce service
      const recommended = this.getRecommendedQuantity(productId, serviceName);
      // Utiliser la recommandation si disponible, sinon 0 (pas product.quantity qui est le stock total)
      const defaultPrepared = recommended ?? 0;
      return {
        productId: productId,
        productName: product.name,
        recommended: recommended, // Recommandation calculée par le moteur
        prepared: defaultPrepared, // R1: product.quantity par défaut
        sold: undefined as any, // Non saisi = undefined
        wasted: undefined as any, // Non saisi = undefined
        hasRupture: false,
      };
    });
  }

  /**
   * Finaliser l'entrée en mode saisie après chargement de tous les services
   */
  finalizeSalesEntry(): void {
    // Ouvrir le premier accordéon par défaut
    if (this.selectedServices.length > 0) {
      this.expandedService = this.selectedServices[0];
    }

    this.step = 'enter-sales';
    this.loading = false;
  }

  /**
   * Charger un brouillon existant
   */
  loadDraft(draft: any): void {
    this.productSales = draft.products || [];
    this.contextFlags = draft.contextFlags || this.contextFlags;
    this.notes = draft.notes || '';
    this.actualAttendance = draft.actualAttendance || '';
    this.actualWeather = draft.actualWeather || '';
    this.isDraft = true;
    this.log.debug('Brouillon chargé:', draft);
  }

  /**
   * Initialiser saisie vide - avec quantité de setup comme recommandation
   */
  initializeEmptySales(): void {
    this.productSales = this.products.map((product) => {
      const productId = (product as any)._id || '';
      // Utiliser Product.quantity (quantité setup) comme recommandation
      const recommended = product.quantity || null;
      return {
        productId: productId,
        productName: product.name,
        recommended: recommended, // Quantité setup du marchand
        prepared: 0,
        sold: 0,
        wasted: 0,
        hasRupture: false,
      };
    });
  }

  /**
   * Étape 2 → 3: Valider quantités
   */
  proceedToContext(): void {
    const validationError = this.dailySalesService.validateProducts(
      this.productSales,
    );
    if (validationError) {
      this.error = validationError;
      return;
    }

    // Saisie partielle autorisée (service sans vente = données valides)
    this.error = '';
    this.step = 'context';
  }

  /**
   * Étape 3 → 4: Résumé
   */
  proceedToSummary(): void {
    this.step = 'summary';
  }

  /**
   * Revenir en arrière
   */
  goBack(): void {
    if (this.step === 'enter-sales') {
      this.step = 'select-date';
    } else if (this.step === 'context') {
      this.step = 'enter-sales';
    } else if (this.step === 'summary') {
      this.step = 'context';
    }
  }

  /**
   * Calculer quantité gaspillée automatiquement et valider cohérence
   */
  onPreparedOrSoldChange(product: ProductSale): void {
    // Valider que vendu <= préparé
    if (product.sold != null && product.sold > product.prepared) {
      product.sold = product.prepared; // Limiter automatiquement
    }

    // Calculer gaspillage
    if (product.sold != null && product.prepared >= product.sold) {
      product.wasted = product.prepared - product.sold;
    }
  }

  /**
   * Copier la quantité préparée vers vendu (= tout vendu)
   */
  setSoldToPrepared(product: ProductSale): void {
    product.sold = product.prepared;
    product.wasted = 0;
  }

  /**
   * Marquer comme épuisé avec rupture (demande non satisfaite)
   */
  setExhausted(product: ProductSale): void {
    product.sold = product.prepared;
    product.wasted = 0;
    product.hasRupture = true;
  }

  /**
   * Définir le restant en pourcentage (ex: 5% restant = 95% vendu)
   */
  setRemaining(product: ProductSale, remainingPercent: number): void {
    const soldPercent = 100 - remainingPercent;
    product.sold = Math.round((product.prepared * soldPercent) / 100);
    product.wasted = product.prepared - product.sold;
    product.hasRupture = false; // Décocher la case rupture
  }

  /**
   * Sauvegarder comme brouillon - utilise nouveau DailySalesService
   * Crée un brouillon par service sélectionné
   */
  saveDraft(): void {
    this.loading = true;
    this.error = '';

    const draftPromises: Promise<any>[] = [];

    // Créer un brouillon par service
    for (const serviceName of this.selectedServices) {
      const serviceProducts = this.productSalesByService.get(serviceName) || [];

      // Préparer les produits (accepte tout, même vide pour brouillon)
      const productsToSubmit = serviceProducts.map((p) => {
        // Trouver le prix du produit depuis this.products
        const product = this.products.find((prod) => prod.id === p.productId);
        const unitPrice = product?.sellPrice || 0;

        return {
          productId: p.productId,
          productName: p.productName,
          quantityRecommended: p.recommended || undefined,
          quantityPrepared: p.prepared,
          quantitySold: p.sold ?? 0,
          quantityWasted: p.wasted ?? 0,
          unitPrice: unitPrice,
          hasRupture: p.hasRupture,
        };
      });

      // Créer le payload pour ce service
      const payload: DailySalesPayload = {
        merchantType: 'fixed',
        source: 'manual',
        saleDate: this.selectedDate,
        serviceName: serviceName,
        products: productsToSubmit,
        contextFlags: this.contextFlags,
        notes: this.notes,
        actualAttendance: this.actualAttendance,
        actualWeather: this.actualWeather,
        isDraft: true,
      };

      draftPromises.push(this.dailySalesService.saveDraft(payload).toPromise());
    }

    Promise.all(draftPromises)
      .then((drafts) => {
        this.log.debug(`${drafts.length} brouillon(s) sauvegardé(s):`, drafts);
        this.loading = false;
        this.successMessage = `Brouillon sauvegardé pour ${this.selectedServices.length} service(s)`;
        this.cdr.markForCheck();

        // Masquer le message après 3 secondes
        setTimeout(() => {
          this.successMessage = '';
          this.cdr.markForCheck();
        }, 3000);
      })
      .catch((err) => {
        this.log.error('Erreur sauvegarde brouillon:', err);
        this.error = err.error?.error || 'Erreur sauvegarde brouillon';
        this.loading = false;
        this.cdr.markForCheck();
      });
  }

  /**
   * Soumettre (finaliser) - utilise nouveau DailySalesService + validation
   * Crée une DailySales par service sélectionné
   */
  submit(): void {
    this.loading = true;
    this.error = '';

    // Créer une vente par service sélectionné ET par date (si période)
    const submissionPromises: Promise<any>[] = [];

    // Calculer toutes les dates de la période
    const datesToSubmit: string[] = [];
    if (
      this.periodType === 'custom' &&
      this.customPeriod.from &&
      this.customPeriod.to
    ) {
      const startDate = new Date(this.customPeriod.from);
      const endDate = new Date(this.customPeriod.to);

      let currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        datesToSubmit.push(currentDate.toISOString().split('T')[0]);
        currentDate.setDate(currentDate.getDate() + 1);
      }
    } else {
      // Une seule date
      datesToSubmit.push(this.selectedDate);
    }

    this.log.debug(
      `📅 Création de ventes pour ${datesToSubmit.length} date(s):`,
      datesToSubmit,
    );

    // Boucle sur chaque date, puis sur chaque service
    for (const date of datesToSubmit) {
      for (const serviceName of this.selectedServices) {
        const serviceProducts =
          this.productSalesByService.get(serviceName) || [];

        // Filtrer et préparer les produits pour l'envoi
        const productsToSubmit = serviceProducts
          .filter((p) => p.prepared > 0 || (p.sold ?? 0) > 0)
          .map((p) => {
            // Trouver le prix du produit depuis this.products
            const product = this.products.find(
              (prod) => prod.id === p.productId,
            );
            const unitPrice = product?.sellPrice || 0;

            return {
              productId: p.productId,
              productName: p.productName,
              quantityRecommended: p.recommended || undefined,
              quantityPrepared: p.prepared,
              quantitySold: p.sold ?? 0, // Convertir undefined en 0
              quantityWasted: p.wasted ?? 0, // Convertir undefined en 0
              unitPrice: unitPrice,
              hasRupture: p.hasRupture,
            };
          });

        // Créer le payload pour ce service ET cette date
        const payload: DailySalesPayload = {
          merchantType: 'fixed',
          source: 'manual',
          saleDate: date, // Date de la boucle
          serviceName: serviceName,
          products: productsToSubmit,
          contextFlags: this.contextFlags,
          notes: this.notes,
          actualAttendance: this.actualAttendance,
          actualWeather: this.actualWeather,
          isDraft: false,
        };

        // Ajouter à la file d'attente si des produits à enregistrer
        if (productsToSubmit.length > 0) {
          submissionPromises.push(
            this.dailySalesService.create(payload).toPromise(),
          );
        }
      }
    }

    // Vérifier qu'il y a au moins une soumission
    if (submissionPromises.length === 0) {
      this.error =
        'Aucun produit à enregistrer (préparé ou vendu doit être > 0)';
      this.loading = false;
      return;
    }

    // Envoyer toutes les créations en parallèle
    Promise.all(submissionPromises)
      .then((sales: any[]) => {
        this.log.debug(`${sales.length} vente(s) créée(s):`, sales);

        // Valider seulement les ventes non encore validées
        const validationPromises = sales
          .filter((sale) => !sale.isValidated) // Ne valider que si pas déjà validé
          .map((sale) => this.dailySalesService.validate(sale._id).toPromise());

        return Promise.all(validationPromises);
      })
      .then((validations) => {
        this.log.debug('Toutes les ventes validées:', validations);
        this.loading = false;
        this.analytics.trackVenteSoumise(this.selectedServices.length);

        // Afficher message de succès
        this.successMessage = 'Ventes sauvegardées';
        this.cdr.markForCheck();

        // Redirection après 2 secondes
        setTimeout(() => {
          this.router.navigate(['/dashboard']);
        }, 2000);
      })
      .catch((err) => {
        this.log.error('Erreur soumission ventes:', err);
        this.error = err.error?.error || 'Erreur enregistrement ventes';
        this.loading = false;
        this.cdr.markForCheck();
      });
  }

  /**
   * Annuler
   */
  cancel(): void {
    if (
      confirm(
        'Êtes-vous sûr de vouloir annuler? Les modifications non sauvegardées seront perdues.',
      )
    ) {
      this.router.navigate(['/dashboard']);
    }
  }

  /**
   * Calculer total préparé (tous services confondus)
   */
  getTotalPrepared(): number {
    let total = 0;
    this.productSalesByService.forEach((products) => {
      total += products.reduce((acc, p) => acc + (p.prepared || 0), 0);
    });
    return total;
  }

  /**
   * Calculer total vendu (tous services confondus)
   */
  getTotalSold(): number {
    let total = 0;
    this.productSalesByService.forEach((products) => {
      total += products.reduce((acc, p) => acc + (p.sold ?? 0), 0);
    });
    return total;
  }

  /**
   * Calculer total gaspillé (tous services confondus)
   */
  getTotalWasted(): number {
    let total = 0;
    this.productSalesByService.forEach((products) => {
      total += products.reduce((acc, p) => acc + (p.wasted ?? 0), 0);
    });
    return total;
  }

  /**
   * Compter le nombre de produits en rupture (tous services confondus)
   */
  getTotalRuptures(): number {
    let total = 0;
    this.productSalesByService.forEach((products) => {
      total += products.filter((p) => p.hasRupture).length;
    });
    return total;
  }

  getTotalRevenue(): number {
    let total = 0;
    this.productSalesByService.forEach((products) => {
      products.forEach((p) => {
        const prod = this.products.find((pr) => pr.id === p.productId);
        total += (p.sold ?? 0) * (prod?.sellPrice || 0);
      });
    });
    return total;
  }

  getTotalWasteValue(): number {
    let total = 0;
    this.productSalesByService.forEach((products) => {
      products.forEach((p) => {
        const prod = this.products.find((pr) => pr.id === p.productId);
        total +=
          (p.wasted ?? 0) * (prod?.purchasePrice || prod?.sellPrice || 0);
      });
    });
    return total;
  }
}
